import '@babylonjs/core/Meshes/thinInstanceMesh';
import type { TileMap } from '@archon3d/core';
import { Constants } from '@babylonjs/core/Engines/constants';
import { Light } from '@babylonjs/core/Lights/light';
import { PointLight } from '@babylonjs/core/Lights/pointLight';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { RawTexture } from '@babylonjs/core/Materials/Textures/rawTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';
import { CAMERA_ELEVATION } from './arenaCamera';

/** How many lamps light the fighters for real at once: two is the effect that matters (doc 12). */
export const LIT_LAMPS = 2;
/** The ground's glow reaches this share of a lamp's range. */
const GLOW_SHARE = 0.7;
/** A lamp's flame: its size, and how far over the lamp's top it burns, in metres. */
const FLAME_SIZE = 0.9;
/** How strongly a lamp shows by day (`night` 0) and by moonlight (`night` 1). */
const GLOW_BY_DAY = 0.35;
const LIGHT_BY_DAY = 0.35;
const LIGHT_BY_NIGHT = 1.2;
const SIZE = 64;

export interface Lamp {
  x: number;
  z: number;
  /** The top of the lamp's tile, where its flame burns. */
  y: number;
  color: Color3;
  range: number;
}

/** Every lamp an arena's tiles carry (`light` on a tile family), at the middle of its tile. */
export function lampsOf(map: Pick<TileMap, 'width' | 'height' | 'tile'>): Lamp[] {
  const lamps: Lamp[] = [];
  for (let y = 0; y < map.height; y++)
    for (let x = 0; x < map.width; x++) {
      const tile = map.tile(x, y);
      if (!tile.light) continue;
      lamps.push({
        x: x + 0.5,
        z: y + 0.5,
        y: tile.walk === 'blocked' && tile.height > 0 ? tile.height : 0,
        color: Color3.FromHexString(tile.light.color),
        range: tile.light.range,
      });
    }
  return lamps;
}

/** The `count` lamps nearest any of the points (the living fighters), nearest first. */
export function nearestLamps(
  lamps: readonly Lamp[],
  points: readonly { x: number; z: number }[],
  count = LIT_LAMPS,
): Lamp[] {
  if (points.length === 0) return [];
  const distance = (lamp: Lamp) =>
    Math.min(...points.map((p) => Math.hypot(p.x - lamp.x, p.z - lamp.z)));
  return [...lamps].sort((a, b) => distance(a) - distance(b)).slice(0, count);
}

/** A disc of light: full in the middle, falling off smoothly to nothing at its edge. */
function glowTexture(scene: Scene): Texture {
  const data = new Uint8Array(SIZE * SIZE * 4);
  for (let y = 0; y < SIZE; y++)
    for (let x = 0; x < SIZE; x++) {
      const r = Math.hypot((x + 0.5) / SIZE - 0.5, (y + 0.5) / SIZE - 0.5) / 0.5;
      const k = Math.max(0, 1 - r);
      const i = (y * SIZE + x) * 4;
      data[i] = data[i + 1] = data[i + 2] = 255;
      data[i + 3] = Math.round(255 * k * k);
    }
  const texture = RawTexture.CreateRGBATexture(
    data,
    SIZE,
    SIZE,
    scene,
    true,
    false,
    Texture.TRILINEAR_SAMPLINGMODE,
  );
  texture.name = 'arenaLampGlow';
  texture.hasAlpha = true;
  return texture;
}

/**
 * The arena's lamps (content:quality#2, x-aaa-gap#10), whose `light` was authored on the tiles and
 * drawn by nothing, as doc 12 has them:
 * - every lamp has a flame over its top and a glow on the ground round it, in its colour: all of
 *   them thin instances of one additive quad, one draw call however many lamps;
 * - the two lamps nearest the fighters are real point lights, chosen again every frame, so a
 *   fighter walking past one is lit by it. Forty real lights would cost the Quest its frame.
 *
 * The flames stand turned to the duel's fixed camera. By moonlight the glows and the lights are
 * at their strongest; by day they are faint.
 */
export class ArenaLamps {
  private readonly mesh: Mesh | null;
  private readonly material: StandardMaterial | null;
  private readonly colors: Float32Array;
  private readonly lights: PointLight[] = [];
  private night = 0;

  constructor(
    scene: Scene,
    private readonly parent: TransformNode,
    private readonly lamps: readonly Lamp[],
    /** Meshes the lamps must not light: the room round a duel on the table. */
    excluded: readonly AbstractMesh[] = [],
  ) {
    this.colors = new Float32Array(lamps.length * 2 * 4);
    if (lamps.length === 0) {
      this.mesh = null;
      this.material = null;
      return;
    }
    this.mesh = MeshBuilder.CreateGround('arenaLampGlow', { width: 2, height: 2 }, scene);
    this.mesh.parent = parent;
    this.mesh.isPickable = false;
    // The fixed camera sees the whole arena; the base quad's own box would cull the glows.
    this.mesh.alwaysSelectAsActiveMesh = true;
    const material = new StandardMaterial('arenaLampGlow', scene);
    material.disableLighting = true;
    material.emissiveColor = Color3.White();
    material.opacityTexture = glowTexture(scene);
    // Light adds to what is under it.
    material.alphaMode = Constants.ALPHA_ADD;
    material.disableDepthWrite = true;
    material.backFaceCulling = false;
    // Lying just over the floor, it is drawn over it rather than fighting it for depth.
    material.zOffset = -2;
    this.material = material;
    this.mesh.material = material;

    // One glow on the ground and one flame in the air per lamp.
    const matrices = new Float32Array(lamps.length * 2 * 16);
    const matrix = new Matrix();
    const upright = Quaternion.RotationAxis(Vector3.Right(), CAMERA_ELEVATION - Math.PI / 2);
    lamps.forEach((lamp, i) => {
      // The quad is two metres across: scaled by the glow's reach, that is its radius.
      const reach = lamp.range * GLOW_SHARE;
      Matrix.ComposeToRef(
        new Vector3(reach, 1, reach),
        Quaternion.Identity(),
        new Vector3(lamp.x, 0.02, lamp.z),
        matrix,
      );
      matrix.copyToArray(matrices, i * 2 * 16);
      const half = FLAME_SIZE / 2;
      Matrix.ComposeToRef(
        new Vector3(half, 1, half),
        upright,
        new Vector3(lamp.x, lamp.y + FLAME_SIZE / 2, lamp.z),
        matrix,
      );
      matrix.copyToArray(matrices, (i * 2 + 1) * 16);
    });
    this.mesh.thinInstanceSetBuffer('matrix', matrices, 16, true);
    this.writeColors();
    this.mesh.thinInstanceSetBuffer('color', this.colors, 4, false);

    for (let i = 0; i < Math.min(LIT_LAMPS, lamps.length); i++) {
      const light = new PointLight(`arenaLamp${i}`, Vector3.Zero(), scene);
      light.parent = parent;
      light.intensity = 0;
      // Fading out over the lamp's range for the fighters' glTF materials as for the floor's, not
      // by the inverse square, which leaves a lamp's light almost nothing a few metres off.
      light.falloffType = Light.FALLOFF_STANDARD;
      light.excludedMeshes = [...excluded];
      light.setEnabled(false);
      this.lights.push(light);
    }
  }

  /** Point lights that stand for the lamps, whether lit this frame or not. */
  get pointLights(): readonly PointLight[] {
    return this.lights;
  }

  /** How dark the duel's square is: 0 at noon, 1 by moonlight. */
  setNight(night: number): void {
    this.night = Math.min(1, Math.max(0, night));
    if (!this.mesh) return;
    this.writeColors();
    this.mesh.thinInstanceBufferUpdated('color');
  }

  /** Call once per rendered frame with the living fighters' places on the floor. */
  update(fighters: readonly { x: number; z: number }[]): void {
    const lit = nearestLamps(this.lamps, fighters, this.lights.length);
    // A lamp's reach is in the world's metres, and the arena may be a model on the table.
    const scale = Math.abs(this.parent.scaling.x) || 1;
    const intensity = LIGHT_BY_DAY + (LIGHT_BY_NIGHT - LIGHT_BY_DAY) * this.night;
    this.lights.forEach((light, i) => {
      const lamp = lit[i];
      if (!lamp) {
        light.intensity = 0;
        return;
      }
      light.position.set(lamp.x, lamp.y + FLAME_SIZE / 2, lamp.z);
      light.diffuse.copyFrom(lamp.color);
      lamp.color.scaleToRef(0.3, light.specular);
      light.range = lamp.range * scale;
      light.intensity = intensity;
    });
  }

  setEnabled(on: boolean): void {
    this.mesh?.setEnabled(on);
    for (const light of this.lights) light.setEnabled(on);
  }

  dispose(): void {
    for (const light of this.lights) light.dispose();
    this.material?.opacityTexture?.dispose();
    this.material?.dispose();
    this.mesh?.dispose();
  }

  private writeColors(): void {
    const glow = GLOW_BY_DAY + (1 - GLOW_BY_DAY) * this.night;
    this.lamps.forEach((lamp, i) => {
      this.colors.set([lamp.color.r, lamp.color.g, lamp.color.b, glow], i * 2 * 4);
      this.colors.set([lamp.color.r, lamp.color.g, lamp.color.b, 1], (i * 2 + 1) * 4);
    });
  }
}
